import { useMutation, useQuery } from '@apollo/client';
import { Button, Card, CardContent, Tooltip, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { getMediaFileKey } from 'shared/common/file-storage';
import {
    amRequestedInfo,
    awaitingAMApproval,
    rocketScreenCompleteStage,
    waitingForDocsStage
} from 'shared/models/job-stages';

import { Editor } from 'react-text-editor';

import { crossAddCandidate, moveCandidateToStage } from '../../actions';
import { deleteDraft, updateDraft } from '../../common/draft-storage';
import { FileDownloadLink } from '../../core-ui/file-download-link';
import {
    AgentDraftSubmission,
    ExistingSubmissionNote,
    GET_AGENT_DRAFT_SUBMISSION,
    GET_EXISTING_SUBMISSION_NOTE,
    MARK_AGENT_DRAFT_SUBMISSION_USED
} from '../../graphql/queries/agent';
import { useAgent } from '../../hooks/use-agent';
import { useSlides } from '../../hooks/use-candidate-slides';
import { useReduxDispatch, useReduxState } from '../../hooks/use-redux';
import { useSession } from '../../hooks/use-session';
import { NoteDraftState } from '../../state';
import { NoteDialog } from '../note-dialog';

interface SubmissionDraftFeedProps {
    draftSubmissionId: string;
}

const DRAFT_SKELETON_BODY_HEIGHT = 140;

function isOnCandidatePage(pathname: string, personId: string, jobId: string): boolean {
    const expectedPath = `/job/${jobId}/board/candidate/${personId}`;
    return pathname === expectedPath;
}

export const SubmissionDraftFeed: React.FC<SubmissionDraftFeedProps> = ({ draftSubmissionId }) => {
    const [noteDialogOpen, setNoteDialogOpen] = React.useState(false);
    const location = useLocation();
    const agent = useAgent();
    const dispatch = useReduxDispatch();
    const { setList } = useSlides();
    const { user } = useSession();
    const jobStages = useReduxState((state) => state.appConstants.constants.jobStages);

    const { data: draftData, loading: draftLoading } = useQuery<{ draft: AgentDraftSubmission | null }>(
        GET_AGENT_DRAFT_SUBMISSION,
        {
            variables: { id: draftSubmissionId }
        }
    );

    const personId = draftData?.draft?.personId;
    const jobId = draftData?.draft?.jobId;

    // Fetch existing submission note via GraphQL (not Redux)
    const { data: existingNoteData } = useQuery<{ notes: ExistingSubmissionNote[] }>(GET_EXISTING_SUBMISSION_NOTE, {
        skip: !personId || !jobId,
        variables: { personId: `persons-${personId}`, jobId }
    });

    const [markUsed] = useMutation(MARK_AGENT_DRAFT_SUBMISSION_USED);

    // Check if we're on the orbit page (where we should NOT close the agent)
    const isOnOrbitPage = location.pathname.startsWith('/orbit');

    const draft = draftData?.draft || null;
    const existingNote = existingNoteData?.notes?.[0] ?? null;

    const showDraftSkeleton = draftLoading || !draft;

    if (showDraftSkeleton) {
        return (
            <Card variant="outlined" className="agent__emailPreviewCard">
                <CardContent className="agent__emailPreviewCardContent">
                    <div className="agent__emailPreviewHeader">
                        <Typography variant="subtitle1" className="agent__emailPreviewSubject">
                            Submission Note
                        </Typography>
                    </div>
                    <Skeleton variant="rect" width="100%" height={DRAFT_SKELETON_BODY_HEIGHT} />
                </CardContent>
            </Card>
        );
    }

    const noteDraftKey = `submission-note-submit-${personId}-${jobId}`;

    const isSubmittingExistingCandidate = () =>
        draft?.candidate &&
        allowedStages.includes(draft?.candidate?.stage?.name) &&
        draft.candidate.assignee === user.id;

    const isCrossSubmit = () => {
        const amRequestedInfoStage = jobStages.find((s) => s.name === amRequestedInfo);
        return !draft?.candidate || (amRequestedInfoStage && draft?.candidate?.stage?.id <= amRequestedInfoStage.id);
    };

    const handleOpenNoteDialog = async () => {
        markUsed({ variables: { id: draftSubmissionId, usedAt: Date.now() } });

        // Overwrite local storage draft with the agent-generated content
        const draftState: NoteDraftState = {
            addingAttachment: false,
            content: draft.body,
            context: { isSubmissionNote: true, jobId: draft.jobId },
            initialAttachments: (draft.attachments ?? []).map((a) => ({
                filename: a.filename,
                key: getMediaFileKey(a.key),
                size: a.size
            })),
            modifiedAt: Date.now(),
            saving: false
        };
        await updateDraft(noteDraftKey, draftState);

        // If not on the candidate page, open the candidate slide first
        if (draft?.candidate && !isOnCandidatePage(location.pathname, personId, jobId)) {
            const candidateKey = { personId, jobId };
            setList([candidateKey], candidateKey);
        }

        // Close orbit dialog if not on the /orbit page
        if (!isOnOrbitPage) {
            agent.onClose();
        }
        setNoteDialogOpen(true);
    };

    const handleNoteDialogCancel = () => {
        deleteDraft(noteDraftKey);
        setNoteDialogOpen(false);
    };

    const handleNoteDialogSave = () => {
        deleteDraft(noteDraftKey);
        setNoteDialogOpen(false);

        // Move candidate to awaiting AM approval stage after saving the submission note
        if (isSubmittingExistingCandidate()) {
            // Candidate exists in target job - move their stage
            if (draft.candidate.stage.name !== awaitingAMApproval) {
                dispatch(moveCandidateToStage({ jobId: draft.jobId, personId: draft.personId }, awaitingAMApproval));
            }
        } else if (isCrossSubmit()) {
            // Candidate doesn't exist in target job - cross-add from source job
            dispatch(
                crossAddCandidate(draft.personId, [draft.jobId], draft.sourceJobId, awaitingAMApproval, 'cross-submit')
            );
        } else {
            throw new Error('Cannot use submission draft for this candidate/job');
        }
    };

    const allowedStages = [rocketScreenCompleteStage, waitingForDocsStage];

    const canUseSubmission = isSubmittingExistingCandidate() || isCrossSubmit();

    const getDisabledReason = () => {
        if (!draft) return 'Loading candidate...';
        if (draft?.candidate && !allowedStages.includes(draft?.candidate?.stage?.name)) {
            return 'Candidate must be in "Rocket Screen Complete" or "Waiting for Docs" stage';
        }
        return '';
    };
    const disabledReason = getDisabledReason();

    const openNoteButton = (
        <div className="agent__emailSendButtonContainer">
            <Tooltip title={canUseSubmission ? 'Open this draft in the submission note editor' : disabledReason}>
                <span>
                    <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        onClick={handleOpenNoteDialog}
                        className="agent__emailPreviewButton"
                        disabled={!canUseSubmission}
                    >
                        Use Submission
                    </Button>
                </span>
            </Tooltip>
        </div>
    );

    const editorContent = <Editor value={draft.body} disabled={true} className="agent__emailEditor" />;

    const attachmentsList = (draft.attachments ?? []).map((a) => (
        <FileDownloadLink key={a.key} path={getMediaFileKey(a.key)} filename={a.filename}>
            <div className="note-attachment" style={{ cursor: 'pointer' }}>
                <div className="list-centered-icon">
                    <i className="fas fa-file-alt attachment-icon" />
                    <span>{a.filename}</span>
                </div>
            </div>
        </FileDownloadLink>
    ));

    const attachmentsSection =
        attachmentsList.length > 0 ? (
            <div className="note-attachments-section" style={{ marginLeft: 0 }}>
                {attachmentsList}
            </div>
        ) : null;

    // Convert draft attachments to NoteAttachment format (add media/files prefix for S3 access)
    const initialAttachments = (draft.attachments ?? []).map((a) => ({
        filename: a.filename,
        key: getMediaFileKey(a.key),
        size: a.size
    }));

    const initialDraft: NoteDraftState = {
        addingAttachment: false,
        content: draft.body,
        context: { isSubmissionNote: true, jobId: draft.jobId },
        initialAttachments,
        modifiedAt: Date.now(),
        saving: false
    };

    // Use existing note ID if present (for updating existing submission note)
    const existingNoteId = existingNote?.id;

    const noteDialog = noteDialogOpen ? (
        <NoteDialog
            initialDraft={initialDraft}
            personId={draft.personId}
            jobId={draft.jobId}
            noteId={existingNoteId}
            noteDraftKey={noteDraftKey}
            title="Submission Note"
            onCancel={handleNoteDialogCancel}
            onSave={handleNoteDialogSave}
        />
    ) : null;

    return (
        <>
            <Card variant="outlined" className="agent__emailPreviewCard">
                <CardContent className="agent__emailPreviewCardContent">
                    <div className="agent__emailPreviewHeader">
                        <Typography variant="subtitle1" className="agent__emailPreviewSubject">
                            Submission Note
                        </Typography>
                    </div>
                    {editorContent}
                    {attachmentsSection}
                    {openNoteButton}
                </CardContent>
            </Card>
            {noteDialog}
        </>
    );
};
